import throttle from 'lodash.throttle';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { getHomepageUrl } from '@cartwhl/common/utils';
import { findPairIndexByAnimationNumber } from '@cartwhl/common/utils/chatRecords';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Loading from '@cartwhl/ui/atoms/Loading';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import CameraCancelledIcon from '@cartwhl/ui/icons/CameraCancelledIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import Button from '@cartwhl/ui/input/Button';
import PlayerDialog from '@cartwhl/ui/molecules/PlayerDialog';

import {
  animationGenerationStore,
  animationPlayerStore,
  projectStore,
  sceneSettingsStore,
} from 'stores';
import { deleteJobFromBackend } from 'stores/animationGenerationStore';

// @deprecated: use VideoOverlay from Timeline instead
export default function VideoOverlays() {
  const { videoUpload, jobData, currentAnimationNumber } = useSnapshot(
    animationGenerationStore.state,
  );
  const { mediaUpload } = useSnapshot(projectStore.state);
  const currentAnimation = jobData?.variationInfoByID[currentAnimationNumber];

  if (
    videoUpload?.status === 'PROCESSING' ||
    videoUpload?.status === 'QUEUED' ||
    mediaUpload?.status === 'PROCESSING' ||
    mediaUpload?.status === 'QUEUED' ||
    mediaUpload?.status === 'UPLOADING'
  ) {
    return <WaitingOverlay />;
  }

  if (currentAnimation?.comicMp4Path && jobData?.jobID) {
    const index = findPairIndexByAnimationNumber(
      currentAnimationNumber,
      jobData.jobID,
      jobData.chatHistory,
    );
    if (index > 0) {
      return <Disclaimer />;
    }

    return (
      <SyncedVideo
        key={currentAnimation.comicMp4Path}
        videoUrl={currentAnimation.comicMp4Path}
        mediaId={currentAnimation?.comicMediaID}
      />
    );
  }
}

const SyncedVideo = ({
  videoUrl,
  mediaId,
}: {
  videoUrl: string;
  mediaId?: string;
}) => {
  const { currentFrame, duration } = useSnapshot(animationPlayerStore.state);
  const {
    playbackSettings: { frameRate },
  } = useSnapshot(sceneSettingsStore.state);

  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [canPlayThrough, setCanPlayThrough] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  const videoRef = useRef<HTMLVideoElement>(null);

  const throttledFrameUpdate = useMemo(
    () =>
      throttle((time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      }, 16), // ~60fps (1000ms/60)
    [],
  );

  useEffect(() => {
    const updateVideoTime = () => {
      if (
        !canPlayThrough ||
        !videoDuration ||
        !videoRef.current ||
        duration.seconds === 0
      )
        return;

      const frameTime = 1 / frameRate;
      const durationQuotient = videoDuration / duration.seconds;
      const time = frameTime * currentFrame * durationQuotient;

      requestAnimationFrame(() => {
        throttledFrameUpdate(time);
      });
    };

    updateVideoTime();
  }, [
    currentFrame,
    frameRate,
    duration.seconds,
    videoDuration,
    canPlayThrough,
    throttledFrameUpdate,
  ]);

  // Add a handler to download the video file when called.
  const handleDownloadVideo = () => {
    if (!videoUrl) return;
    // Create an anchor element to download the video file
    const a = document.createElement('a');
    a.href = videoUrl;
    // Try to give a reasonable file name based on the videoUrl
    const urlParts = videoUrl.split('/');
    let fileName = urlParts[urlParts.length - 1];
    if (!fileName.endsWith('.mp4')) fileName += '.mp4';
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <>
      <div
        className={`absolute top-4 right-4 z-10 flex items-start justify-end rounded-2xl transition-all ease-linear ${
          expanded
            ? 'h-[220px] w-[220px] sm:h-[316px] sm:w-[316px] xl:h-[440px] xl:w-[440px] 2xl:h-[640px] 2xl:w-[640px]'
            : 'h-[90px] w-[90px] sm:h-[124px] sm:w-[124px] xl:h-[164px] xl:w-[164px] 2xl:h-[200px] 2xl:w-[200px]'
        }`}
      >
        <div className="group relative">
          {mediaId && canPlayThrough && (
            <Dropdown
              trigger={
                <Button
                  variant="tertiary"
                  size="sm"
                  className="pointer-events-auto absolute top-0.5 right-0.5 z-20 lg:top-2 lg:right-2 lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100"
                  icon={<ThreeDotsIcon />}
                />
              }
            >
              <Dropdown.Item
                onClick={() => {
                  window.open(
                    getHomepageUrl(
                      `/profile/uploaded-media?mediaId=${mediaId}`,
                    ),
                    '_blank',
                  );
                }}
              >
                Go to original video
              </Dropdown.Item>
              <Dropdown.Item onClick={handleDownloadVideo}>
                Download
              </Dropdown.Item>
            </Dropdown>
          )}
          <Tooltip
            text="Expand video reference"
            side="left"
            disabled={expanded}
          >
            <button
              className="group-hover:bg-gray-90/20 absolute top-0 right-0 bottom-0 left-0 z-10 rounded-2xl transition-colors"
              onClick={() => setExpanded((prev) => !prev)}
            />
          </Tooltip>
          <video
            key={videoUrl}
            className="h-full w-full rounded-2xl transition-all"
            ref={videoRef}
            playsInline
            muted
            crossOrigin="use-credentials"
            preload="auto"
            onLoadedMetadata={() => {
              if (videoRef.current) {
                setVideoDuration(videoRef.current?.duration);
                // Get native video dimensions
                const videoWidth = videoRef.current.videoWidth;
                const videoHeight = videoRef.current.videoHeight;
                const aspectRatio = videoWidth / videoHeight;
                setAspectRatio(aspectRatio);
              }
            }}
            onCanPlayThrough={() => setCanPlayThrough(true)}
            style={{
              width: aspectRatio > 1 ? '100%' : 'auto',
              height: aspectRatio > 1 ? 'auto' : '100%',
            }}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        </div>
        {!canPlayThrough && (
          <div className="flex h-1/3 w-1/2 flex-none items-center justify-center rounded-lg bg-white/20">
            <Loading />
          </div>
        )}
      </div>
    </>
  );
};

const Disclaimer = () => {
  return (
    <Tooltip
      text="Video comparison for stitched motions coming soon..."
      side="left"
    >
      <div className="absolute top-4 right-4 z-10 flex h-[60px] w-[90px] flex-none items-center justify-center rounded-lg bg-white/20">
        <CameraCancelledIcon />
      </div>
    </Tooltip>
  );
};

const WaitingOverlay = () => {
  const { videoUpload, jobData } = useSnapshot(animationGenerationStore.state);
  const { mediaUpload } = useSnapshot(projectStore.state);

  if (!videoUpload && !mediaUpload) return null;

  let title = '';
  if (videoUpload?.status === 'QUEUED' || mediaUpload?.status === 'QUEUED') {
    title = 'Media generation queued… please wait';
  } else if (
    videoUpload?.status === 'PROCESSING' ||
    mediaUpload?.status === 'PROCESSING'
  ) {
    title = 'Processing media...';
  } else if (
    videoUpload?.status === 'UPLOADING' ||
    mediaUpload?.status === 'UPLOADING'
  ) {
    title = 'Uploading media...';
  }

  const handleCancel = async () => {
    if (jobData?.jobID) {
      await deleteJobFromBackend(jobData.jobID);
      animationGenerationStore.removeUpload();
    }
  };

  return (
    <PlayerDialog
      title={title}
      open={true}
      maxWidth={500}
      zValue={20}
      alignMobile="end"
    >
      <div
        className={`bg-green/20 relative h-24 overflow-hidden rounded-2xl p-4`}
      >
        <div className="flex h-full w-full items-center justify-between gap-1">
          <div className="flex flex-col items-start gap-1">
            {(videoUpload?.status === 'UPLOADING' ||
              mediaUpload?.status === 'UPLOADING') && (
              <>
                <Text variant="studio/body/medium/sm">Uploading media... </Text>
              </>
            )}

            {(videoUpload?.status === 'QUEUED' ||
              mediaUpload?.status === 'QUEUED') && (
              <>
                <Text variant="studio/body/medium/sm">
                  Your request has been queued...
                </Text>
                {!!jobData?.estimatedQueueWaitTime && (
                  <Text
                    variant="studio/body/sm"
                    className="text-gray-60 dark:text-gray-45"
                  >
                    Estimated queue time:{' '}
                    <span>
                      {Math.round(jobData.estimatedQueueWaitTime)} seconds
                    </span>
                  </Text>
                )}
              </>
            )}
            {(videoUpload?.status === 'PROCESSING' ||
              mediaUpload?.status === 'PROCESSING') && (
              <>
                <Text variant="studio/body/medium/sm">
                  Processing media...{' '}
                  <Percentage
                    value={
                      videoUpload?.processingPercentage ||
                      mediaUpload?.processingPercentage ||
                      0
                    }
                  />
                </Text>

                {(!!videoUpload?.estimatedProcessingTime ||
                  !!mediaUpload?.estimatedProcessingTime) && (
                  <Text
                    variant="studio/body/sm"
                    className="text-gray-60 dark:text-gray-45"
                  >
                    Estimated processing time:{' '}
                    <span>
                      {Math.round(
                        (videoUpload?.estimatedProcessingTime ||
                          mediaUpload?.estimatedProcessingTime ||
                          0) / 1000,
                      )}{' '}
                      seconds
                    </span>
                  </Text>
                )}
              </>
            )}
          </div>
          <div>
            <Button
              aria-label="Cancel video generation"
              variant="tertiary"
              size="sm"
              icon={<CrossIcon className="mx-0.5 h-4 w-4" />}
              onClick={handleCancel}
            />
          </div>
        </div>
        <div
          className="bg-green ease absolute bottom-0 left-0 h-[3px] transition-[width]"
          style={{
            width: `${
              videoUpload?.processingPercentage ||
              mediaUpload?.processingPercentage
            }%`,
          }}
        />
      </div>
    </PlayerDialog>
  );
};

function Percentage({ value }: { value: number | undefined }) {
  if (!value) return <span className="inline-block w-12 text-left"></span>;
  return <span className="inline-block w-12 text-left">{value}%</span>;
}
